let _                  = require('../../libs/lodash');
let DateRangeExtractor = require('./date-range-extractor');

////////////////////////////////////////////////////////////////
// User group dataset
let userGroups         = require('../lookups/resource-group');

/**
 * Process a list of activities and format them into a
 * logical grouping for display. It is used to format the
 * data coming from a report service.
 */
class ActivityProcessor {
    
    /**
     * Group activities based on a target field.
     * 
     * @param {array}  activities - List of activities
     * @param {string} field      - Field as key
     * 
     * @returns {array}
     */
    groupBy(activities, field) {
        return _.groupBy(activities, field);
    }

    /**
     * Return the classification of the project
     * (billable, non-billable, non-productive)
     * 
     * @param {string} projectName - Project name
     * 
     * @return {string}
     */
    classifyProject(projectName) {

        if (_.isNull(projectName) || !_.isString(projectName)) {
            throw 'Invalid project name';
        }
        
        return (projectName.match(/internal/ig)) ?
            (projectName.match(/breaks/ig) ? 'non-productive' : 'non-billable') : 
            'billable';
    }

    /**
     * Return a processed dataset based on the activities provided.
     * It groups the list of activities by project and do neccessary
     * computation to prepare the data for display.
     * 
     * @param {array}  activities - List of activities
     * @param {string} dateFrom   - Start of date range
     * @param {string} dateTo     - End of date range
     * 
     * @return {object}
     */
    processActivitiesOfResource(activities, dateFrom, dateTo) {
        let that               = this;
        let groupedActivities  = this.groupBy(activities, 'projectId');

        let dateRangeExtractor = new DateRangeExtractor();
        let weekdays           = dateRangeExtractor.getWeekdays(dateFrom, dateTo);

        let processedGroupActivities = {};
        let mergedPlottedActivities  = null;

        let map = {
            'billable'       : 'Billable',
            'non-billable'   : 'Non-Billable',
            'non-productive' : 'Non-Productive'
        };

        _.each(groupedActivities, (activities, projectId) => {
            
            let projectName             = activities[0].projectName;
            let totalTimeSpentOnGroup   = that.getTotalTimeSpent(activities);
            let plottedActivitiesOnDate = that.plotActivitiesToDate(activities, weekdays);
            let type                    = that.classifyProject(projectName);
            
            mergedPlottedActivities = (mergedPlottedActivities === null) ? 
                plottedActivitiesOnDate :
                that.mergePlottedActivities(mergedPlottedActivities, plottedActivitiesOnDate);
            
            processedGroupActivities[projectId] = {
                project     : {
                    projectId : projectId,
                    name      : projectName,
                },
                activities  : activities,
                type        : type,
                typeLabel   : map[type],
                datePlot    : {
                    plot    : plottedActivitiesOnDate,
                    date    : _.keys(plottedActivitiesOnDate),
                    values  : _.values(plottedActivitiesOnDate)
                },
                timeSpent: {
                    seconds : totalTimeSpentOnGroup * 3600,
                    hours   : totalTimeSpentOnGroup
                } 
            }
        });

        let aggregatedTimeSpentByType = this.aggregateTimeSpent(processedGroupActivities, 'type');
        let timeSpentOnAllActivities  = this.getTotalTimeSpent(activities);

        return {
            group             : processedGroupActivities,
            plottedActivities : {
                plot          : mergedPlottedActivities,
                date          : _.keys(mergedPlottedActivities),
                values        : _.values(mergedPlottedActivities) 
            },
            aggregatedSummary : aggregatedTimeSpentByType,
            timeSpent         : {
                seconds       : timeSpentOnAllActivities * 3600,
                hours         : timeSpentOnAllActivities        
            }
        };
    }

    /**
     * Process the activities done on the project.
     * 
     * @param {array}  activities - List of activties to process
     * @param {string} dateFrom   - Start of date range
     * @param {string} dateTo     - End of date range
     * 
     * @return {object}
     */
    processActivitiesOfProject(activities, dateFrom, dateTo) {

        let resourceActivities = this.groupBy(activities, 'resource');

        let dateRangeExtractor = new DateRangeExtractor();
        let weekdays           = dateRangeExtractor.getWeekdays(dateFrom, dateTo);

        let processedResourceActivities = {};
        let mergedPlottedActivities     = null;
        
        _.each(resourceActivities, (activities, resource) => {

            let totalTimeSpentOnGroup   = this.getTotalTimeSpent(activities);
            let plottedActivitiesOnDate = this.plotActivitiesToDate(activities, weekdays);

            mergedPlottedActivities = (mergedPlottedActivities === null) ? 
                plottedActivitiesOnDate :
                this.mergePlottedActivities(mergedPlottedActivities, plottedActivitiesOnDate);

            processedResourceActivities[resource] = {
                group       : userGroups[`@${resource}`],
                activities  : activities,
                datePlot    : {
                    plot    : plottedActivitiesOnDate,
                    date    : _.keys(plottedActivitiesOnDate),
                    values  : _.values(plottedActivitiesOnDate)
                },
                timeSpent: {
                    seconds : totalTimeSpentOnGroup * 3600,
                    hours   : totalTimeSpentOnGroup
                } 
            }
        });

        let aggregatedTimeSpentByGroup = this.aggregateTimeSpent(processedResourceActivities, 'group');
        let timeSpentOnAllActivities   = this.getTotalTimeSpent(activities);

        return {
            resources         : processedResourceActivities,
            plottedActivities : {
                plot          : mergedPlottedActivities,
                date          : _.keys(mergedPlottedActivities),
                values        : _.values(mergedPlottedActivities) 
            },
            aggregatedSummary : aggregatedTimeSpentByGroup,
            timeSpent         : {
                seconds       : timeSpentOnAllActivities * 3600,
                hours         : timeSpentOnAllActivities        
            }
        }
    }
    
    aggregateTimeSpent(activities, field) {

        let collatedSummary = {};
        _.each(activities, (value) => {

            let fieldValue = value[field];
            
            if (_.isUndefined(collatedSummary[fieldValue])) {
                collatedSummary[fieldValue] = {
                    timeSpent: _.clone(value.timeSpent)
                };

                return;
            }

            collatedSummary[fieldValue].timeSpent.hours   += value.timeSpent.hours
            collatedSummary[fieldValue].timeSpent.seconds += value.timeSpent.seconds
        });

        return collatedSummary;
    }

    /**
     * Return a merged version of all plotted activities into a single
     * collection.
     * 
     * @param {array} combinedPlot      - Reduced plot of total values
     * @param {array} plottedActivities - Plot data to be merged
     * 
     * @return {array}
     */
    mergePlottedActivities(combinedPlot, plottedActivities) {        
        _.each(plottedActivities, (val, key) => {
            combinedPlot[key] += val;
        });

        return combinedPlot;
    }

    /**
     * Return the total time spent on each activity per day
     * 
     * @param {array} activities - List of activities
     * @param {array} dates      - List of dates
     * 
     * @return {array}
     */
    plotActivitiesToDate(activities, dates) {

        let plottedDates = {};

        _.each(dates, (date) => {
            date                     = date.format('YYYY-MM-DD');
            let activitiesDoneOnDate = _.filter(activities, {"date": date});

            plottedDates[date]       = this.getTotalTimeSpent(activitiesDoneOnDate);
        });

        return plottedDates;
    }

    /**
     * Return the computed time spent on all activities listed
     * 
     * @param {array} activities - Activtities
     * 
     * @return {number}
     */
    getTotalTimeSpent(activities) {
        return _.reduce(activities, (totalTime, activity) => {
            return totalTime + activity.timeSpent.hours
        }, 0)
    }

}

module.exports = ActivityProcessor;